import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Bookmark, ChevronDown, Trash2, Loader2, X, Save } from "lucide-react";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TemplateBar({ formType, onLoad }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!formType) return;
    authFetch(`${API}/api/templates?cert_type=${encodeURIComponent(formType)}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { templates: [] })
      .then((d) => setTemplates(d.templates || []))
      .catch(() => {});
  }, [formType]);

  const loadTemplate = (tpl) => {
    setLoading(true);
    onLoad(tpl.data || {});
    toast.success(`Template "${tpl.name}" loaded`);
    setOpen(false);
    setLoading(false);
  };

  const deleteTemplate = async (name) => {
    try {
      await authFetch(`${API}/api/templates/${encodeURIComponent(name)}`, { method: "DELETE", credentials: "include" });
      setTemplates((prev) => prev.filter((t) => t.name !== name));
      toast.success(`Template "${name}" deleted`);
    } catch { toast.error("Failed to delete"); }
  };

  if (templates.length === 0) return null;

  return (
    <div className="relative mb-4" data-testid="template-bar">
      <button onClick={() => setOpen(!open)} data-testid="template-bar-toggle"
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all"
        style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }}>
        <Bookmark className="w-3.5 h-3.5" />
        Load from template ({templates.length})
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 w-72 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 4px 20px var(--shadow-color)' }}>
          <div className="max-h-48 overflow-y-auto">
            {templates.map((t, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2.5 border-b last:border-0 hover:opacity-80 transition-opacity" style={{ borderColor: 'var(--border-light)' }}>
                <button onClick={() => loadTemplate(t)} className="flex-1 text-left min-w-0" data-testid={`load-tpl-${i}`}>
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--text-primary)' }}>{t.name}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {t.created_at ? new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : ""}
                  </p>
                </button>
                <button onClick={() => deleteTemplate(t.name)} className="p-1 rounded-lg text-red-400 hover:bg-red-50 flex-shrink-0" data-testid={`del-tpl-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {loading && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#00A3E0]" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SaveTemplateButton({ formType, getFormData }) {
  const [saving, setSaving] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [name, setName] = useState("");

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Enter a template name"); return; }
    setSaving(true);
    try {
      await authFetch(`${API}/api/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim(), cert_type: formType, data: getFormData() }),
      });
      toast.success(`Template "${name}" saved!`);
      setShowInput(false);
      setName("");
    } catch { toast.error("Failed to save template"); }
    finally { setSaving(false); }
  };

  if (showInput) {
    return (
      <div className="flex items-center gap-2" data-testid="save-template-form">
        <input
          data-testid="template-name-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name..."
          className="rounded-xl border px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#00A3E0] w-40"
          style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
        />
        <button onClick={handleSave} disabled={saving} data-testid="confirm-save-template"
          className="p-2 rounded-xl text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => { setShowInput(false); setName(""); }} className="p-2 rounded-xl" style={{ color: 'var(--text-muted)' }}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => setShowInput(true)} data-testid="save-as-template-btn"
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
      style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }}>
      <Bookmark className="w-3.5 h-3.5" /> Save as Template
    </button>
  );
}
